/**
 * Query Dependencies — Single Source of Truth for Cache Invalidation
 *
 * Defines, per entity type, the full set of query keys that must be invalidated
 * when that entity changes.
 *
 * **This file is the only place to edit when:**
 *   - adding a new query key that needs cascading invalidation, or
 *   - changing what a mutation should refresh.
 *
 * Two consumption paths both delegate here:
 *   1. `createMutationHook({ invalidateKeys: getDependencies('contact', matterId) })`
 *      via `lib/services/mutation-utils.ts`
 *   2. `invalidateAfterX(queryClient, matterId)` helpers in
 *      `lib/query-keys/invalidation-helpers.ts` — thin delegators around
 *      `getDependencies(...)`.
 *
 * Raw array shapes (vs. factory readonly tuples) are intentional: they keep
 * `getBatchDependencies` JSON-stringification dedupe stable across factory
 * output and inline keys, and they preserve historical shape variants (e.g.
 * string vs. number `matterId` in `['matter-institutions', ...]`) that
 * different consumers subscribe to.
 *
 * **Exception — the communications keys go through their factory** (LF-6001).
 * Those tuples became owner-scoped (`'matter' | 'lead'` + id) so a lead id
 * cannot collide with a matter id, and a raw literal here is invisible to the
 * type checker: the previous `['communications', 'thread-items', matterId]`
 * would have kept compiling while silently no longer prefix-matching anything,
 * so the thread would just stop refreshing. A factory call makes the next shape
 * change a compile error instead. The emitted array is identical, so the
 * `getBatchDependencies` stringify dedupe above is unaffected.
 *
 * Import the communications factory from its own module, never the
 * `@/lib/query-keys` barrel — the barrel re-exports `invalidation-helpers`,
 * which imports this file, so the barrel would close a cycle.
 *
 * @see lib/query-keys/CLAUDE.md — single-source rule
 */

import type { QueryKey } from '@tanstack/react-query';
import { communicationsKeys } from '@/lib/query-keys/communications';
import { distributionStatementKeys } from '@/lib/query-keys/distribution-statements';
import { correspondenceKeys } from '@/lib/query-keys/correspondence';
import { institutionKeys } from '@/lib/query-keys/institutions';
import { accountKeys } from '@/lib/query-keys/account';
import { matterOwner } from '@/lib/services/communications/owner';

/**
 * Function that returns query keys that depend on an entity
 */
export type DependencyResolver = (
  matterId?: number,
  entityId?: number | string
) => QueryKey[];

// ─── Shared key bundles ────────────────────────────────────────────────────

/**
 * Matter-level keys touched by virtually every matter-scoped mutation:
 * matter detail (carries stages.progress + stages.requiresAttention), the
 * estate-admin overview used by the Next Steps card, the direct-fetch overview
 * page, and timeline / progress.
 */
const matterScopedKeys = (matterId: number): QueryKey[] => [
  ['matters', 'detail', matterId],
  ['matter-estate-admin', matterId, 'overview'],
  ['matters', matterId, 'overview'],
  ['overview', matterId],
  ['progress', matterId],
  ['timeline-events', matterId],
  ['history', matterId]
];

/**
 * The full task-cache surface for a matter. Includes both the `taskKeys.*`
 * factory shapes and the legacy `task-dashboard` / `institution-tasks` shapes
 * that some consumers still subscribe to.
 */
const matterTaskKeys = (matterId: number): QueryKey[] => [
  ['tasks', 'matter', matterId],
  ['tasks', 'list', matterId],
  ['tasks', 'dashboard', matterId],
  ['tasks', 'summary', matterId],
  ['matter', matterId, 0, 'tasks-dashboard-data'],
  ['matter', matterId, 1, 'tasks-dashboard-data'],
  ['matter', matterId, 'institution-tasks-dashboard'],
  ['institution-tasks', matterId],
  ['task-dashboard', String(matterId)]
];

/**
 * The matter's document list and its counts: what a generated document reaches
 * because it is an ordinary matter `File`, and a regeneration archives the one
 * it replaces. Shared by every surface that generates a document onto the
 * matter (precedent documents, court forms), so the two cannot drift.
 */
const matterGeneratedDocumentKeys = (matterId: number): QueryKey[] => [
  ['documents', 'list', matterId],
  ['files', 'list', matterId],
  ['matters', matterId, 'documents-summary'],
  ['sidebar-counts', matterId]
];

// ─── Entity dependency map ─────────────────────────────────────────────────

export const QUERY_DEPENDENCIES: Record<string, DependencyResolver> = {
  /**
   * Identity variant — an extra name / address / holding reference on a person
   * (LF-5566 PROV-21). Polymorphic over its owner, so the two positional args
   * carry the OWNER, not a matter: `getDependencies('identity-variant', ownerId,
   * ownerType)` where `ownerType` is 'matter' (the deceased) or 'contact'.
   *
   * Only the owner's own variant list depends on this — no entity screen renders
   * variants outside that section today, and the downstream consumers (the
   * share-registry identity union, the court-form alias recital) read them
   * server-side at generation time rather than from a cached query.
   */
  'identity-variant': (ownerId, ownerType) => {
    if (ownerId == null || ownerType == null) return [];
    return [['identity-variants', ownerType, ownerId]];
  },

  /**
   * Inventory item — discovery / collection lifecycle.
   * Status changes affect tasks, correspondence workflows, institution forms,
   * redemption visibility, and the entitlement waterfall.
   */
  'inventory-item': (matterId, itemId) => {
    if (matterId == null) return [];
    return [
      ...matterScopedKeys(matterId),

      // Direct inventory caches
      ['inventory', 'list', matterId],
      ['inventory', 'list', matterId, 'current-view'],
      ['inventory', 'item', matterId, itemId].filter(Boolean),
      ['inventory-items', matterId],

      // A planning matter's Inventory tab groups by where each asset goes,
      // which turns on the holding mode stored on the item itself.
      ['planning', matterId, 'asset-destinations'],

      // Matter overview / metric aggregates
      ['matters', matterId, 'summary'],
      ['matters', matterId, 'metrics'],
      ['matters', matterId, 'inventory-summary'],

      // Institution lookups (both shape variants exist in consumers)
      ['matter-institutions', matterId],
      ['matter-institutions', matterId.toString()],

      ...matterTaskKeys(matterId),

      // Correspondence (status changes trigger workflow eligibility)
      ['matters', matterId.toString(), 'correspondences'],
      ['correspondence', matterId],
      ['correspondence', 'list', matterId],
      ['correspondence', 'threads', matterId],
      ['correspondence', 'workflow', matterId],
      ['institutions', matterId, 'correspondence'],
      ['workflow', 'correspondences', matterId],
      ['workflow', 'correspondence', matterId],
      ['workflow', 'data', matterId, 'notification'],
      ['workflow', 'data', matterId, 'collection'],

      // Discovery / redemption tab state depends on inventory status
      ['redemption', matterId],
      ['discovery', matterId],

      // Forms — required form set depends on linked holdings
      ['institution-forms', matterId],
      ['institution-forms-all', matterId],

      // Entitlement waterfall recomputes from inventory totals
      ['entitlements', 'waterfall', matterId],

      // Distribution planning BFF derives availableToDistribute and the
      // secured-debt rule from inventory state.
      ['distributions', 'planning', matterId],

      // The Statement's live account is assembled from inventory realisations:
      // an item edit can add a receipts line (a realised value recorded), date
      // one (a `receivedAt` writes the backing receipt), or clear its
      // unbacked-realisation diagnostic. Only the preview — an issued statement
      // is a frozen snapshot no inventory change can move.
      //
      // Via the factory, not a literal, for the same reason as the
      // communications keys above: a raw tuple would go on compiling after the
      // key shape moved, and the account would just stop refreshing.
      distributionStatementKeys.preview(matterId),

      // Sidebar count badge
      ['sidebar-counts', matterId]
    ];
  },

  /**
   * Contact — beneficiary, LPR, or professional.
   * Adding/changing contacts unlocks tasks (e.g. LPR-gated work) and forms.
   */
  contact: (matterId, contactId) => {
    if (matterId == null) return [];
    return [
      ...matterScopedKeys(matterId),

      ['contacts', 'list', matterId],
      ['contacts', 'item', matterId, contactId].filter(Boolean),
      ['matters', matterId, 'representatives'],
      ['matters', matterId, 'beneficiaries'],

      // Court forms: a proving executor is a Form 105 deponent and names the
      // Form 101 applicants (VALE-1993).
      ['court-forms', matterId],

      ...matterTaskKeys(matterId),

      // Forms cache (contact-driven form availability)
      ['institution-forms', matterId],
      ['institution-forms-all', matterId],
      ['forms', 'list', matterId],
      ['forms', 'by-contact', contactId].filter(Boolean),
      ['forms', 'by-institution'],
      ['institution', 'forms'],
      ['matters', matterId, 'forms'],
      ['forms', 'summary', matterId],

      // Partial-match catch for matter-scoped sub-trees
      ['matter', matterId],

      /*
        The planning record and its package (VALE-1764).

        **A planning testator IS a contact**, and so is every person a will
        appoints, so a contact write moves what those surfaces render more
        directly than it moves most of the keys above it. Deleting one left the
        Instructions columns, the drafting list and the execution list all
        showing the person, with their name, until the page was reloaded.

        The package too, because it hangs off the testator: the documents are
        drafted for a person, and a stale list is a will belonging to nobody.
      */
      ['planning', matterId],
      ['planning-package', matterId],
      // The execution record names people too: an attendee and an appointee
      // who has accepted are read through their contact ([EPLAN-26]).
      ['planning-execution', matterId],

      ['sidebar-counts', matterId]
    ];
  },

  /**
   * Task — completion, assignment, or scheduling change.
   * Affects stage progress + Next Steps card.
   */
  task: (matterId, taskId) => {
    if (matterId == null) return [];
    return [
      ...matterScopedKeys(matterId),

      ['tasks', 'item', matterId, taskId].filter(Boolean),
      ['matters', matterId, 'stages'],

      ...matterTaskKeys(matterId)
    ];
  },

  /**
   * Document / file — upload, delete, OCR completion.
   * Crucial documents (e.g. death certificate) gate task availability.
   */
  document: (matterId, documentId) => {
    if (matterId == null) return [];
    return [
      ...matterScopedKeys(matterId),

      ['documents', 'list', matterId],
      ['documents', 'item', matterId, documentId].filter(Boolean),
      ['files', 'list', matterId],
      ['matters', matterId, 'documents-summary'],
      ['forms', 'list', matterId],
      ['correspondence', 'list', matterId],

      ...matterTaskKeys(matterId),

      ['sidebar-counts', matterId]
    ];
  },

  /**
   * Entitlement — beneficiary share, class, or waterfall change.
   * Drives downstream distribution + release calculations.
   */
  entitlement: (matterId, entitlementId) => {
    if (matterId == null) return [];
    return [
      ...matterScopedKeys(matterId),

      ['entitlements', 'list', matterId],
      ['entitlements', 'detail', matterId, entitlementId].filter(Boolean),
      ['entitlements', 'detail'], // base — covers `useEntitlement` "details" pending key
      ['entitlements', 'waterfall', matterId],
      ['entitlements', 'classes', matterId],

      // Distribution planning BFF is computed from entitlements server-side,
      // so any entitlement change must also invalidate the planning cache.
      ['distributions', 'planning', matterId],

      ['sidebar-counts', matterId]
    ];
  },

  /**
   * Planning fact or lifecycle change on an estate-planning matter
   * ([EPLAN-2]).
   *
   * **One key, because the backend serves one bundle.** `GET /planning` returns
   * both testators, the shared children and the lifecycle together — the
   * workspace renders the two testators side by side, so a per-collection read
   * would make "which facts belong to whom" a frontend join. Any write
   * therefore refreshes the whole record.
   *
   * It also invalidates entitlements: a planning entitlement is attributed to
   * one testator, so deleting a testator cascades to their entitlement rows
   * server-side and the structural view would otherwise show gifts belonging to
   * nobody. `matterScopedKeys` is deliberately **not** included — nothing on the
   * matter row, the estate-admin overview or the timeline reads planning facts,
   * and invalidating them on every checkbox would refetch six queries to no
   * effect.
   */
  planning: (matterId) => {
    if (matterId == null) return [];
    return [
      ['planning', matterId],
      // The ledger's derived half reads the assets and the engagement as well
      // as the documents ([EPLAN-21]), so recording who holds an asset clears
      // the open item that asked.
      ['planning-ledger', matterId],
      ['entitlements', 'list', matterId],
      ['entitlements', 'detail'],
      // The generated package hangs off the testators: deleting one cascades to
      // their documents server-side, so a stale package would list a will
      // belonging to nobody ([EPLAN-8]).
      ['planning-package', matterId],
      // Who owes an acceptance is read from the appointments, so an edit to an
      // attorney moves the execution record ([EPLAN-26]).
      ['planning-execution', matterId],
      // The contacts list, because a planning write creates people: an apply
      // and the workspace's own pickers both make a contact for a person named
      // in a will rather than storing a bare name (VALE-1764), and the Contacts
      // tab has to show them.
      ['contacts', 'list', matterId],
      ['sidebar-counts', matterId],
      // The matter detail, for `stages[]`: the planning sidebar draws a
      // progress bar per stage from it, and the Instructions bar is computed
      // from exactly these facts (VALE-1764).
      ['matters', 'detail', matterId]
    ];
  },

  /**
   * Generated planning package change — a generation, a Word save-back, or an
   * executed mark ([EPLAN-8]).
   *
   * Wider than `planning` above in two directions that both matter:
   *
   * - **The Documents surfaces.** Generating produces real `File` rows on the
   *   matter and a regeneration archives the pair it replaced, so the Documents
   *   tab, its count badge and the documents summary are all now wrong.
   * - **The planning record.** A first generation advances the lifecycle
   *   `instructions → drafting`, and the stage strip reads it from the bundle.
   *
   * `matterScopedKeys` stays out for the same reason it does above — the matter
   * row and the estate-admin overview do not read planning documents.
   */
  planningPackage: (matterId) => {
    if (matterId == null) return [];
    return [
      ['planning-package', matterId],
      ['planning', matterId],
      // The open-items ledger derives half its rows from the documents
      // ([EPLAN-21]), so resolving a clause choice clears a ledger row and the
      // list has to be refetched to notice.
      ['planning-ledger', matterId],
      // The execution record reads the executed marks: a document marked
      // signed outside an event is unwitnessed, and "not signed after all"
      // takes it off its event ([EPLAN-26]).
      ['planning-execution', matterId],
      ['documents', 'list', matterId],
      ['files', 'list', matterId],
      ['matters', matterId, 'documents-summary'],
      ['sidebar-counts', matterId],
      // The matter detail, because it carries `stages[]` and the planning
      // sidebar now draws a progress bar per stage from it (VALE-1764). Marking
      // a document signed is exactly the act that moves the Execution bar, and
      // without this the bar sat unchanged until the page was reloaded. The
      // rest of `matterScopedKeys` stays out for the reason below: the matter
      // row and the estate-admin overview do not read planning documents.
      ['matters', 'detail', matterId]
    ];
  },

  /**
   * A write to the execution record: a signing event, its witnessing, an
   * acceptance or a paper record ([EPLAN-26]).
   *
   * **The execution key itself is not here**, because every one of these
   * writes answers with the whole record and the hooks write it straight into
   * the cache. What is here is what the record moves elsewhere:
   *
   * - **The package**, because placing a document at an event marks it signed
   *   on the event's date and sets its `signingEventId`.
   * - **The ledger**, because an outstanding acceptance is one of its items.
   * - **The matter detail**, for `stages[]`: the Execution marker reads the
   *   same criterion the record serves as `complete`.
   */
  planningExecution: (matterId) => {
    if (matterId == null) return [];
    return [
      ['planning-package', matterId],
      ['planning-ledger', matterId],
      ['matters', 'detail', matterId]
    ];
  },

  /**
   * A comment on a drafting decision, posted, edited, deleted, resolved or
   * re-opened ([EPLAN-18]).
   *
   * **The matter-wide prefix, not one thread's key.** The list is fetched per
   * `(testator, documentType)`, so the review screen and any workspace-level
   * count hold several entries at once, and two of the five writes move a
   * thread the caller did not name: resolving stamps every comment in the
   * thread together, and posting re-opens a resolved one. React Query matches by
   * prefix, so `['planning-decision-comments', matterId]` reaches every filtered
   * list on the matter and none on another.
   *
   * Nothing else depends on a comment. A thread argues about a decision; it
   * does not change the planning facts, the package, or any count outside this
   * surface, so the wider planning bundles stay out.
   */
  'planning-decision-comment': (matterId) => {
    if (matterId == null) return [];
    return [['planning-decision-comments', matterId]];
  },

  /**
   * A document generated from one of the firm's own precedents changed,
   * a generation, a regeneration, or a Word save-back (VALE-809).
   *
   * Wider than the generated-documents list itself, because generating produces
   * real `File` rows on the matter and a regeneration archives the pair it
   * replaced: the Documents tab, its count badge and the documents summary are
   * all now wrong. Unlike `planningPackage` there is no lifecycle to advance —
   * an estate-administration matter has no stage that a generated document moves
   * — so the matter bundle stays out.
   */
  precedentDocuments: (matterId) => {
    if (matterId == null) return [];
    return [
      ['precedent-documents', matterId],
      ...matterGeneratedDocumentKeys(matterId)
    ];
  },

  /**
   * A generated Queensland court form changed: a generation, a regeneration, or
   * a Word save-back (VALE-1992).
   *
   * The court-forms list itself plus `matterGeneratedDocumentKeys`: the
   * generated `.docx` is an ordinary matter file, and a regeneration archives
   * the one it replaces. Nothing on the grant stage moves (lodging is its own
   * write), so the grant keys stay out.
   */
  courtForms: (matterId) => {
    if (matterId == null) return [];
    return [
      ['court-forms', matterId],
      ...matterGeneratedDocumentKeys(matterId)
    ];
  },

  /**
   * Matter — top-level matter fields (status flags, dates).
   * Cascades into stages, grants, navigation, and task availability.
   */
  matter: (matterId) => {
    if (matterId == null) return [];
    return [
      ...matterScopedKeys(matterId),

      ['matters', matterId],
      ['matters', 'list'],
      ['matters', matterId, 'details'],
      ['matters', matterId, 'summary'],
      ['matters', matterId, 'metrics'],
      ['matters', matterId, 'stages'],

      // Grant stage data depends on matter fields
      ['grants', 'detail', matterId],

      // Forms — curation reads the matter's probate jurisdiction. The
      // cross-border rule (Section 1071B) compares `matter.state` against each
      // ASX holding's registration state, so a jurisdiction change (the grant
      // workflow's Change Jurisdiction bar writes `decedentAddress.state`,
      // which the API promotes to `matter.state`) recurates the form set.
      // Without this the curated 1071B and its "auto-included" banner persist
      // stale until an unrelated mutation refetches or `staleTime` lapses
      // (VALE-216). Via the factory, not a literal, so a key-shape change is a
      // compile error rather than a silently non-matching prefix.
      institutionKeys.allForms(matterId),

      // Court forms: each row reports the matter details its form is missing
      // (the place of death, the registry), so a fix made in Manage Estate
      // Details must clear the row's "Missing from the matter" (VALE-1993).
      ['court-forms', matterId],

      // Navigation surfaces matter status
      ['navigation', 'matters'],

      ...matterTaskKeys(matterId)
    ];
  },

  /**
   * Correspondence — sent, drafted, or edited.
   * Sending changes institution/inventory state, so the surface is wide.
   */
  correspondence: (matterId, correspondenceId) => {
    if (matterId == null) return [];
    return [
      ...matterScopedKeys(matterId),

      ['correspondence', 'list', matterId],
      ['correspondence', 'item', matterId, correspondenceId].filter(Boolean),
      ['correspondence', 'detail', correspondenceId].filter(Boolean),
      ['correspondence', 'letter-preview', matterId, correspondenceId].filter(
        Boolean
      ),
      // The sign-off blocks rendered INSIDE the letter and email editors. They
      // are resolved backend-side from the correspondence's persisted
      // signatory, so a correspondence change can move them — and unlike the
      // PDF preview they have no nonce to bump, which is why leaving them out
      // showed the previous signatory's signature indefinitely (VALE-1163).
      // Through the key BUILDERS, not a hand-written array: these two live on
      // their own branch of the key tree (`['correspondence', '<preview>', …]`,
      // not under `forMatter`'s `'list'`), so a restated literal here would sit
      // in the list matching nothing and read as covered.
      ...(correspondenceId != null
        ? [
            correspondenceKeys.letterSignOffPreview(
              matterId,
              Number(correspondenceId)
            ),
            correspondenceKeys.emailSignOffPreview(
              matterId,
              Number(correspondenceId)
            )
          ]
        : []),
      ['correspondence', 'threads', matterId],
      ['correspondence', 'workflow', matterId],
      ['workflow', 'correspondences', matterId],
      ['institutions', matterId, 'correspondence'],

      // Discovery / redemption depend on send state
      ['discovery', matterId],
      ['redemption', matterId],

      // Inventory status changes after notification / collection sends
      ['inventory', 'list', matterId],
      ['matter-institutions', matterId],

      // Communications tab — sending or editing a correspondence flips
      // unread/awaiting-reply state on the affected thread. Item keys are
      // keyed by (participantType, participantId) which we don't have here,
      // so we invalidate the whole communications surface for this matter.
      communicationsKeys.threads(matterOwner(matterId)),
      communicationsKeys.threadItemsForOwner(matterOwner(matterId)),

      // Sidebar badge — unread substantive replies + overdue follow-ups
      ['sidebar-counts', matterId],

      ...matterTaskKeys(matterId)
    ];
  },

  /**
   * Institution — bank/registry status, linked correspondence, forms.
   */
  institution: (matterId, institutionId) => {
    if (matterId == null) return [];
    return [
      ...matterScopedKeys(matterId),

      ['institutions', 'list', matterId],
      ['institutions', 'item', matterId, institutionId].filter(Boolean),
      ['institutions', matterId, 'status'],
      ['institutions', matterId, 'correspondence'],
      ['institutions', matterId, 'forms'],
      ['matter-institutions', matterId],
      ['matter-institutions', matterId.toString()],

      // Trust Account balance / proceeds rows depend on the collected amount
      ['inventory', 'list', matterId, 'current-view']
    ];
  },

  /**
   * Contact verification — request / resend / approve lifecycle.
   * Approval writes back to the contact's bankAccount, so contacts list
   * must also be invalidated.
   */
  'contact-verification': (matterId, verificationId) => {
    if (matterId == null) return [];
    return [
      ['contact-verification', 'list', matterId],
      ['contact-verification', 'summary', matterId],
      ['contact-verification', 'detail', matterId, verificationId].filter(
        Boolean
      ),

      // Approval mutates Contact.bankAccount — refresh the contacts list
      ['contacts', 'list', matterId]
    ];
  },

  /**
   * Distribution declaration — sign / cancel / status change.
   * The distribution ABA gate depends on declaration state.
   */
  declaration: (matterId, declarationId) => {
    if (matterId == null) return [];
    return [
      ...matterScopedKeys(matterId),

      ['distribution-declarations', 'list', matterId],
      ['distribution-declarations', 'detail', matterId, declarationId].filter(
        Boolean
      ),
      ['distribution-declarations', 'summary', matterId],

      // Distribution caches (ABA gate depends on declaration status)
      ['distributions', 'list', matterId]
    ];
  },

  /**
   * Distribution — proposed/approved/sent payment to a beneficiary.
   */
  distribution: (matterId, distributionId) => {
    if (matterId == null) return [];
    return [
      ...matterScopedKeys(matterId),

      ['distributions', 'list', matterId],
      ['distributions', 'detail', matterId, distributionId].filter(Boolean),
      ['distributions', 'planning', matterId],

      // Bank-detail write-back may update contact data
      ['contacts', 'list', matterId],

      // Waterfall "distributed to date" depends on distribution statuses
      ['entitlements', 'list', matterId],
      ['entitlements', 'waterfall', matterId],

      // Current View's Distributed section + Trust Account balance
      ['inventory', 'list', matterId, 'current-view'],

      // The Statement's live account is assembled FROM distributions and their
      // retentions: a round's line items become its "Less funds paid" and
      // "Distributions previously made" sections, and a holdback becomes a
      // "Funds held back in trust" line. Missing here, every one of those
      // writes left the estate account showing the previous state until a hard
      // reload — the account is the page the write was made ON, so it is the
      // one surface guaranteed to be in front of the reader at the time.
      //
      // Only the preview: an issued statement is a frozen snapshot that no
      // later round can move, which is the whole point of issuing it.
      //
      // Via the factory rather than a literal tuple, for the reason the
      // inventory resolver gives above — a raw tuple keeps compiling after the
      // key shape moves, and the account just silently stops refreshing.
      distributionStatementKeys.preview(matterId)
    ];
  },

  /**
   * AI Scribe recording — applying changes can import inventory / contacts,
   * so the surface overlaps both. Narrower per-recording keys (list/detail
   * only) are handled by `recording-meta` for non-import mutations.
   */
  recording: (matterId, recordingId) => {
    if (matterId == null) return [];
    return [
      ...matterScopedKeys(matterId),

      ['recordings', 'list', matterId],
      ['recordings', 'detail', recordingId].filter(Boolean),
      ['recordings', 'preview', recordingId].filter(Boolean),

      // Cross-domain imports
      ['inventory', 'list', matterId],
      ['inventory-items', matterId],
      ['contacts', 'list', matterId],

      ['sidebar-counts', matterId]
    ];
  },

  /**
   * Recording metadata-only update (rename, dismiss, retry, reanalyse).
   * Touches the recording caches but no cross-domain entities.
   *
   * Also invalidates `previews` — LF-3052's unified review surface PATCHes
   * lawyer edits to `aiExtractedData` via this dependency set, and those edits
   * are the input to dedup matching. Recomputing preview-changes on the next
   * access keeps inline diff chips honest. Trivial cost on title-only renames.
   */
  'recording-meta': (matterId, recordingId) => {
    if (matterId == null) return [];
    return [
      ['recordings', 'list', matterId],
      ['recordings', 'detail', recordingId].filter(Boolean),
      ['recordings', 'preview', recordingId].filter(Boolean)
    ];
  },

  /**
   * Notification (global, optionally matter-scoped).
   */
  notification: (matterId) => [
    ['notifications'],
    ['notifications', 'unread'],
    ['notifications', 'count'],
    ...(matterId != null
      ? [['notifications', 'matter', matterId] as QueryKey]
      : [])
  ],

  /**
   * Account / law-firm settings — global, no matter context.
   * Task availability can shift globally (e.g. signatory changes).
   */
  account: () => [['account'], ['tasks']],

  /**
   * User profile — global. Affects task assignments and any consumer
   * subscribed to a profile key shape. Every `/profile` consumer (the
   * providers, the account pages and the composer) now shares the single
   * canonical `profileKeys.user()` query (LF-3095); `useCurrentUserId`
   * remains an independent cache that also needs invalidation when the
   * profile changes.
   */
  profile: () => [
    ['profile', 'user'],
    ['profile'],
    ['current-user-id'],
    ['tasks'],
    /*
      The sample-letter render (VALE-1410). It is a server render of the acting
      user's SAVED record — their name, position, office and handwritten
      signature all print on it — so a profile write is exactly what makes the
      stored PDF stale.

      The root rather than `sampleLetter(officeId)`: an invalidation needs the
      prefix, and the parameterised form's trailing `{ officeId: undefined }`
      would match no stored render at all.
    */
    accountKeys.sampleLetterRoot(),
    /*
      The correspondence-setup status and its sample client email (VALE-1633).
      Both read the acting user's saved record: the email is signed with their
      name, and "I don't sign letters" is a preference on it.
    */
    ['onboarding']
  ],

  /**
   * Safe custody packet — law-firm-scoped (not matter-scoped).
   * Partial-match `['safe-custody']` covers list/detail/timeline keys.
   */
  'safe-custody': () => [['safe-custody']],

  /**
   * Communications — mark-read, promote-to-documents, or any mutation that
   * affects thread visibility / read state. Correspondence-driven changes
   * (sends, drafts) go through the `correspondence` resolver above, which
   * also invalidates this surface — this entity exists for Communications-
   * native mutations (mark-read, file-note CRUD downstream of an item).
   */
  communications: (matterId) => {
    if (matterId == null) return [];
    return [
      communicationsKeys.threads(matterOwner(matterId)),
      communicationsKeys.threadItemsForOwner(matterOwner(matterId)),
      ['sidebar-counts', matterId]
    ];
  },

  /**
   * File note — create / edit / soft-delete on a thread. The note lands in
   * the thread items feed and may surface a follow-up timer that drives the
   * sidebar badge through the next sidebar-counts refresh. A timeline event
   * is also generated by the backend.
   */
  'file-note': (matterId, fileNoteId) => {
    if (matterId == null) return [];
    return [
      ...matterScopedKeys(matterId),

      ['file-notes', 'list', matterId],
      ['file-notes', 'detail', matterId, fileNoteId].filter(Boolean),

      // Thread items feed renders file notes inline
      communicationsKeys.threads(matterOwner(matterId)),
      communicationsKeys.threadItemsForOwner(matterOwner(matterId)),

      ['sidebar-counts', matterId]
    ];
  },

  /**
   * Title search — select-candidate, cancel. The mutation either supersedes
   * the row + creates a child search (`select-candidate`) or transitions
   * the row to `cancelled`. Both mutate inventory state downstream (the
   * matter inventory item's status flows through the workflow), so we
   * invalidate the inventory-item dependency set in addition to the
   * title-search-specific shapes.
   *
   * `mark-not-held` is handled as an `inventory-item` mutation rather than
   * a title-search one — see `markInventoryItemNotHeld` in
   * `lib/services/title-searches/hooks.tsx`.
   */
  'title-search': (matterId, searchId) => {
    if (matterId == null) return [];
    return [
      ...matterScopedKeys(matterId),

      // Direct title-search caches
      ['title-searches', 'matter', matterId],
      ['title-searches', 'detail', searchId].filter(Boolean),

      // Inventory state may have flipped (a select-candidate kicks off a
      // fresh workflow that will later update the inventory item)
      ['inventory', 'list', matterId],
      ['inventory-items', matterId],

      // Discovery / matter institutions surface the title-search row
      ['discovery', matterId],
      ['matter-institutions', matterId]
    ];
  },

  /**
   * Timeline event — matter activity / comment.
   * Today's hooks subscribe to string-id key shapes; preserve those
   * alongside the matter-scoped keys.
   */
  'timeline-event': (matterId) => {
    if (matterId == null) return [];
    return [
      ...matterScopedKeys(matterId),
      ['timeline-events', matterId.toString()],
      ['grouped-timeline-events', matterId.toString()],
      ['recent-activity', matterId.toString()]
    ];
  }
};

/**
 * Get all queries that depend on an entity change
 */
export function getDependencies(
  entityType: string,
  matterId?: number,
  entityId?: number | string
): QueryKey[] {
  const resolver = QUERY_DEPENDENCIES[entityType];

  if (!resolver) {
    console.warn(`No dependencies defined for entity type: ${entityType}`);
    return [];
  }

  return resolver(matterId, entityId);
}

/**
 * Get dependencies for multiple entities, deduplicated by JSON shape.
 */
export function getBatchDependencies(
  entities: Array<{
    type: string;
    matterId: number;
    entityId?: number | string;
  }>
): QueryKey[] {
  const allKeys: QueryKey[] = [];
  const keySet = new Set<string>();

  for (const entity of entities) {
    const keys = getDependencies(entity.type, entity.matterId, entity.entityId);

    for (const key of keys) {
      const keyString = JSON.stringify(key);
      if (!keySet.has(keyString)) {
        keySet.add(keyString);
        allKeys.push(key);
      }
    }
  }

  return allKeys;
}
